import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

import { StatusBadge } from '@/lib/components';
import { SortTableArrow } from '@/lib/components/SortTableArrow';
import { Button, Flex, IconSvg, Text } from '@/lib/v2/components';
import { CalendarIcon, GroupsIcon, ReportIcon, StateIcon } from '@/lib/v2/icons/outline';
import {
  FolderIcon,
  FolderToolIcon,
  ListIcon,
  ReportIcon as ReportIconSolid,
} from '@/lib/v2/icons/solid';

import { STRATEGY_MAP_BY_ID } from '@/src/modules/CampaignsModule/components/CampaignsModal/CreateEditCampaigns/constants';
import {
  ECampaignsTypeOptions,
  EStates,
  ICampaign,
} from '@/src/modules/CampaignsModule/interfaces/Campaigns';

import { useHandleEvents } from './useHandleEvents';

import { TitleHeadCell } from '@/modules/CampaignsModule/components/TitleHeadCell';
import { DEFAULT_ORDERBY } from '@/modules/CampaignsModule/constants';
import { useCampaignsMutationContext } from '@/modules/CampaignsModule/contexts/CampaignsContext';
import { DateFormat, formatDate } from '@/modules/CampaignsModule/utils';

export const useCampaignTableColumns = () => {
  const { t } = useTranslation();
  const { changeTablePage, setOrderBy, setClearFilterBy } = useCampaignsMutationContext();
  const { handleClickReport } = useHandleEvents();

  const [tableOrderByList, setTableOrderByList] = useState(DEFAULT_ORDERBY);

  const changeOrderBy = useCallback(
    (element: { id: string; isAsc: boolean | undefined }) => {
      const orderDirectionValue = element.isAsc === true ? 'desc' : 'asc';
      setOrderBy({ sortField: element.id, sortOrder: orderDirectionValue });
    },
    [setOrderBy]
  );

  /** cspell:disable */
  const nameColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<ListIcon />} text={t('CAMPAIGNS_MAIN.TABLE.COLUMNS.name')} />
        <SortTableArrow
          elementName={'name'}
          getTableOrderByList={changeOrderBy}
          setTableOrderByList={setTableOrderByList}
          tableOrderByList={tableOrderByList}
        />
      </Flex>
    ),
    accessor: 'campaign',
    id: 'campaign',
    Cell: ({ cell: { value } }: { cell: { value: ICampaign } }) => (
      <Flex withGap alignment="start">
        <Flex noGrow withGap alignment="start" className="gap-4" gapSize="small">
          {value.campaignTypeId === ECampaignsTypeOptions.MANUAL ? (
            <IconSvg height="auto" svgComponent={<FolderIcon />} width="30px" />
          ) : (
            <IconSvg height="auto" svgComponent={<FolderToolIcon />} width="30px" />
          )}
          <Text
            className="self-start truncate text-emblue-subheadings"
            fontWeight="medium"
            variant="text"
          >
            {value.name}
            <Text className="text-emblue-text" variant="text">
              {value.strategyId &&
                t(
                  `CAMPAIGNS_MAIN.DROPDOWN_STRATEGIES.${
                    STRATEGY_MAP_BY_ID[Number(value.strategyId)]
                  }`,
                  String(value.strategy)
                )}
            </Text>
          </Text>
        </Flex>
      </Flex>
    ),
  };

  const statusColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<StateIcon />} text={t('CAMPAIGNS_MAIN.TABLE.COLUMNS.state')} />
      </Flex>
    ),
    accessor: 'campaign',
    id: 'state',
    Cell: ({ cell: { value } }: { cell: { value: ICampaign } }) => (
      <Flex withGap alignment="start">
        <Flex column noGrow>
          <StatusBadge status={value.state === EStates.ACTIVE ? 'new' : 'inactive'} />
        </Flex>
      </Flex>
    ),
  };

  const lastUpdateDateColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<CalendarIcon />} text={t('CAMPAIGNS_MAIN.TABLE.COLUMNS.update')} />
        <SortTableArrow
          elementName={'lastUpdate'}
          getTableOrderByList={changeOrderBy}
          setTableOrderByList={setTableOrderByList}
          tableOrderByList={tableOrderByList}
        />
      </Flex>
    ),
    accessor: 'campaign',
    id: 'lastUpdate',
    Cell: ({ cell: { value } }: { cell: { value: ICampaign } }) => {
      const formattedDate = value.lastUpdate
        ? formatDate(value.lastUpdate, DateFormat.LongDate)
        : '-';
      return (
        <Flex withGap alignment="start">
          <Flex column noGrow>
            <Text className="self-start text-emblue-subheadings" variant="text">
              {formattedDate}
            </Text>
          </Flex>
        </Flex>
      );
    },
  };

  const durationColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<CalendarIcon />} text={t('CAMPAIGNS_MAIN.TABLE.COLUMNS.duration')} />
        <SortTableArrow
          elementName={'duration'}
          getTableOrderByList={changeOrderBy}
          setTableOrderByList={setTableOrderByList}
          tableOrderByList={tableOrderByList}
        />
      </Flex>
    ),
    accessor: 'campaign',
    id: 'duration',
    Cell: ({ cell: { value } }: { cell: { value: ICampaign } }) => (
      <Flex withGap alignment="start">
        <Flex column noGrow>
          <Text className="self-start text-emblue-subheadings" variant="text">
            {value.duration ? `${value.duration} days` : '-'}
          </Text>
        </Flex>
      </Flex>
    ),
  };

  const audienceColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<GroupsIcon />} text={t('CAMPAIGNS_MAIN.TABLE.COLUMNS.audience')} />
        <SortTableArrow
          elementName={'audience'}
          getTableOrderByList={changeOrderBy}
          setTableOrderByList={setTableOrderByList}
          tableOrderByList={tableOrderByList}
        />
      </Flex>
    ),
    accessor: 'campaign',
    id: 'audience',
    Cell: ({ cell: { value } }: { cell: { value: ICampaign } }) => (
      <Flex withGap alignment="start">
        <Flex column noGrow>
          <Text className="self-start text-emblue-subheadings" variant="text">
            {value.audience || '-'}
          </Text>
        </Flex>
      </Flex>
    ),
  };

  const actionsColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<GroupsIcon />} text={t('CAMPAIGNS_MAIN.TABLE.COLUMNS.actions')} />
        <SortTableArrow
          elementName={'actions'}
          getTableOrderByList={changeOrderBy}
          setTableOrderByList={setTableOrderByList}
          tableOrderByList={tableOrderByList}
        />
      </Flex>
    ),
    accessor: 'campaign',
    id: 'actions',
    Cell: ({ cell: { value } }: { cell: { value: ICampaign } }) => (
      <Flex withGap alignment="start">
        <Flex column noGrow>
          <Text className="self-start text-emblue-subheadings" variant="text">
            {value.actions || 0}
          </Text>
        </Flex>
      </Flex>
    ),
  };

  const buttonColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<ReportIcon />} text={t('CAMPAIGNS_MAIN.TABLE.COLUMNS.report')} />
      </Flex>
    ),
    accessor: 'campaign',
    id: 'buttons',
    Cell: ({ cell: { value } }: { cell: { value: ICampaign } }) => (
      <Flex withGap alignment="start" className="z-10">
        <Button
          outline
          data-id={value.audience}
          id="detail-actions"
          size="small"
          onClick={(event) => handleClickReport(event, value)}
        >
          <IconSvg height="16px" svgComponent={<ReportIconSolid />} width="13px" />
        </Button>
      </Flex>
    ),
  };
  /** cspell:enable */

  const campaignsColumns: Column<object>[] = [
    nameColumn,
    statusColumn,
    lastUpdateDateColumn,
    durationColumn,
    audienceColumn,
    actionsColumn,
    buttonColumn,
  ].filter((x) => x) as Column<object>[];

  return {
    campaignsColumns,
    changeTablePage,
    setClearFilterBy,
  };
};
